import { create } from "zustand";
import type { IndicatorInstance } from "@/types/indicator";

export interface OHLCData {
  open: number;
  high: number;
  low: number;
  close: number;
}

interface ChartState {
  symbol: string;
  activeSymbolName: string;
  timeframe: string;
  chartType: string;
  sourceGranularitySeconds: number;
  hasData: boolean;
  ohlc: OHLCData | null;
  livePrice: number | null;
  priceChange: number | null;
  indicators: IndicatorInstance[];
  oldestEpoch: number | null;
  latestEpoch: number | null;
  hasReachedDepthCeiling: boolean;
  isLoadingHistory: boolean;
  sessionBootstrapped: boolean;
  paneHeights: Record<string, number>; // paneId -> height percentage

  setSymbol: (symbol: string, name?: string) => void;
  setTimeframe: (tf: string) => void;
  setChartType: (type: string) => void;
  setOHLC: (ohlc: OHLCData | null) => void;
  setLivePrice: (price: number | null) => void;
  setPriceChange: (change: number | null) => void;
  addIndicator: (instance: IndicatorInstance) => void;
  removeIndicator: (id: string) => void;
  setIndicators: (instances: IndicatorInstance[]) => void;
  updateIndicator: (id: string, patch: Partial<IndicatorInstance>) => void;
  setPaneHeight: (paneId: string, height: number) => void;
  reset: () => void;
}

const initialState = {
  symbol: "EURUSD",
  activeSymbolName: "EURUSD",
  timeframe: "1h",
  chartType: "candle_solid",
  sourceGranularitySeconds: 60,
  hasData: false,
  ohlc: null as OHLCData | null,
  livePrice: null as number | null,
  priceChange: null as number | null,
  indicators: [] as IndicatorInstance[],
  oldestEpoch: null as number | null,
  latestEpoch: null as number | null,
  hasReachedDepthCeiling: false,
  isLoadingHistory: false,
  sessionBootstrapped: false,
  paneHeights: {} as Record<string, number>,
};

export const useChartStore = create<ChartState>()((set, get) => ({
  ...initialState,

  setSymbol: (symbol, name) =>
    set({
      symbol,
      activeSymbolName: name || symbol,
      sourceGranularitySeconds: 60,
      hasData: false,
      ohlc: null,
      livePrice: null,
      priceChange: null,
      oldestEpoch: null,
      latestEpoch: null,
      hasReachedDepthCeiling: false,
      isLoadingHistory: false,
    }),

  setTimeframe: (tf) => set({ timeframe: tf, ohlc: null }),

  setChartType: (type) => set({ chartType: type }),

  setOHLC: (ohlc) => set({ ohlc }),
  setLivePrice: (price) => set({ livePrice: price }),
  setPriceChange: (change) => set({ priceChange: change }),

  addIndicator: (instance) =>
    set((state) => ({
      indicators: state.indicators.some((i) => i.id === instance.id)
        ? state.indicators
        : [...state.indicators, instance],
    })),

  removeIndicator: (id) =>
    set((state) => ({
      indicators: state.indicators.filter((i) => i.id !== id),
    })),

  setIndicators: (instances) => set({ indicators: instances }),

  updateIndicator: (id, patch) =>
    set((state) => ({
      indicators: state.indicators.map((i) =>
        i.id === id ? { ...i, ...patch } : i,
      ),
    })),

  setPaneHeight: (paneId, height) =>
    set((state) => ({
      paneHeights: { ...state.paneHeights, [paneId]: height },
    })),

  reset: () => set(initialState),
}));
